import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { updateAssessmentEntryMode } from "@/services/repositories/assessment-repository";
import type { Assessment, AssessmentEntryMode } from "@/types/assessment";
import { entryModeLabel, studentWorkspacePaths } from "@/utils/student-workspace";

const MODES: AssessmentEntryMode[] = ["scale", "activity"];

export function AssessmentEntryModeSwitch({
  assessment,
  studentId,
  currentMode,
  beforeSwitch,
}: {
  assessment: Assessment;
  studentId: string;
  currentMode: AssessmentEntryMode;
  beforeSwitch: () => Promise<void>;
}): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [pendingMode, setPendingMode] = useState<AssessmentEntryMode>();
  const [error, setError] = useState<string>();
  const directModeSynced = useRef<string>();

  const switchTo = useCallback(
    async (mode: AssessmentEntryMode, navigateAfterSave: boolean) => {
      if (pendingMode || assessment.status !== "in_progress") return;
      if (mode === currentMode && assessment.lastEntryMode === mode) return;
      setPendingMode(mode);
      setError(undefined);
      try {
        await beforeSwitch();
        const updated = await updateAssessmentEntryMode(assessment.id, mode);
        queryClient.setQueryData(["assessments", assessment.id], updated);
        await queryClient.invalidateQueries({ queryKey: ["assessments"] });
        if (navigateAfterSave && mode !== currentMode) {
          navigate(studentWorkspacePaths.assessment(studentId, mode, assessment.id));
        }
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "切换失败，请重试");
      } finally {
        setPendingMode(undefined);
      }
    },
    [assessment, beforeSwitch, currentMode, navigate, pendingMode, queryClient, studentId],
  );

  useEffect(() => {
    if (
      assessment.recordKind !== "comprehensive" ||
      assessment.status !== "in_progress" ||
      assessment.lastEntryMode === currentMode ||
      directModeSynced.current === `${assessment.id}:${currentMode}`
    ) {
      return;
    }
    directModeSynced.current = `${assessment.id}:${currentMode}`;
    void switchTo(currentMode, false);
  }, [assessment.id, assessment.lastEntryMode, assessment.recordKind, assessment.status, currentMode, switchTo]);

  return (
    <div className="flex flex-col items-start gap-1.5">
      <div
        aria-label="评估录入方式"
        className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1"
      >
        {MODES.map((mode) => (
          <button
            key={mode}
            type="button"
            aria-pressed={mode === currentMode}
            disabled={Boolean(pendingMode) || assessment.status !== "in_progress"}
            onClick={() => void switchTo(mode, true)}
            className={`min-h-9 rounded-md px-3 text-xs font-semibold transition sm:text-sm ${
              mode === currentMode
                ? "bg-white text-brand-dark shadow-sm"
                : "text-slate-500 hover:text-brand-dark"
            } disabled:cursor-not-allowed disabled:opacity-60`}
          >
            {pendingMode === mode ? "切换中…" : entryModeLabel(mode)}
          </button>
        ))}
      </div>
      {error ? (
        <span role="alert" className="text-xs text-red-600">
          {error}
        </span>
      ) : null}
    </div>
  );
}
